import React from 'react';
import dynamic from 'next/dynamic';
import { IUser } from '@sravni/types/lib/auth';
import {
    FORM_FIELDS_PHONES,
    QUERY_PARTNER_KEYS,
    FORM_FIELDS,
    PAGE,
    AMOUNT_LIMITS,
    QUERY_PARAMS,
} from '../../constants';
import { getCommonServerSideProps, collectQueryParams } from '../../helpers/serverside.props';
import { SelectionApp } from '../../types/app';
import { formatPhones, pick, checkEmployerType, removeUndefined, getGlobalRegionFormFields } from '../../helpers';
import { getGlobalRegionForForm } from '../../server/helpers';
import { extractSourceQuery } from '../../helpers/partners';
import { useContentWindowIframeResizer } from '../../components/hooks/iframe.resizer';
import { FormFallbackLoader } from '../../components/landing.common';
import { WhitelabelFormModule } from '../../helpers/dynamic.modules';
import { oldPurposeToNew, PERIOD_SOURCE, PURPOSE_SOURCE_VALUES } from '../../constants/sources';
import { PARTNER_AFF_IDS } from '../../constants/partners';

const WhitelabelForm = dynamic(WhitelabelFormModule, {
    ssr: false,
    loading: () => <FormFallbackLoader />,
});

interface Props {
    account: IUser;
    isMobile: boolean;
    isTablet: boolean;
    memoryData: FE.FormData;
    formData: FE.FormData;
    userId?: string;
    authImport?: boolean;
    pixel?: string;
}

const Whitelable = (props: Props) => {
    useContentWindowIframeResizer({ setStaticIframeScreenSize: true });

    return (
        <React.Fragment>
            {Boolean(props.pixel) && (
                <img
                    style={{
                        display: 'block',
                        position: 'absolute',
                        opacity: '0',
                        left: '0',
                        top: '0',
                    }}
                    src={props.pixel}
                    alt=""
                />
            )}
            <WhitelabelForm {...props} />
        </React.Fragment>
    );
};

const getShowPixel = (ctx: SelectionApp.PageContext) => {
    const options = pick(ctx.query, QUERY_PARTNER_KEYS);
    if (Object.keys(options).length === 0) {
        return null;
    }
    return (
        'https://go.sravni.ru/aff_i?' +
        Object.entries(options)
            .map(([key, value]) => `${key}=${String(value)}`)
            .join('&')
    );
};

export const getServerSideProps = async (ctx: SelectionApp.PageContext) => {
    const page = /\/wl\/agents/.test(ctx.req.url || '') ? PAGE.AGENTS : PAGE.WHITELABEL;
    const data = await getCommonServerSideProps(page)(ctx);
    const { initialState } = data.props;
    const { __WHITELABEL_UID__: userId, __AUTH_IMPORT__: authImport } = ctx.req;
    const {
        [QUERY_PARAMS.PARTNER_KEY]: partnerId,
        [QUERY_PARAMS.AMOUNT]: amount,
        [QUERY_PARAMS.PURPOSE]: purpose,
        [QUERY_PARAMS.PERIOD]: period,
    } = ctx.query || {};

    if (partnerId === PARTNER_AFF_IDS.YURIJ_ISAJ) {
        data.props.initialState.dictionaries.purposes = [
            {
                name: 'Покупка автомобиля',
                value: PURPOSE_SOURCE_VALUES.USED_CAR,
            },
        ];
    }

    const disableMobileAppBanner = [PARTNER_AFF_IDS.ROS_SHTRAFI].includes(partnerId as any);

    const formData = collectQueryParams(ctx);

    initialState.whitelabel.partnerSource = extractSourceQuery(ctx.query);

    formatPhones(formData, FORM_FIELDS_PHONES);
    checkEmployerType(formData);

    const globalRegion = await getGlobalRegionForForm({
        currentLocation: initialState.locations.currentLocation,
        useCurrentLocation: data.props.isLocationManuallySet,
    });

    const getSelectionAmount = () => {
        if (typeof amount === 'string' && !isNaN(parseInt(amount))) {
            return Math.max(Math.min(parseInt(amount), AMOUNT_LIMITS.MAX), AMOUNT_LIMITS.MIN);
        }

        return 500_000;
    };

    const getSelectionPeriod = () => {
        if (typeof period === 'string' && PERIOD_SOURCE.map(({ value }) => value).includes(period.toUpperCase())) {
            return period.toUpperCase();
        }

        return 'P1Y';
    };

    const getSelectionDataPurpose = () => {
        return (
            (typeof purpose === 'string' &&
                Object.values(PURPOSE_SOURCE_VALUES).includes(purpose as ValuesOf<typeof PURPOSE_SOURCE_VALUES>) &&
                oldPurposeToNew(purpose)) ||
            PURPOSE_SOURCE_VALUES.MONEY
        );
    };

    initialState.locations.currentLocation = globalRegion || initialState.locations.currentLocation;

    data.props.formData = formData;
    data.props.initialState.selection.disableMobileAppBanner = disableMobileAppBanner;
    data.props.memoryData = {
        [FORM_FIELDS.AMOUNT]: getSelectionAmount(),
        [FORM_FIELDS.PERIOD]: getSelectionPeriod(),
        [FORM_FIELDS.PURPOSE]: getSelectionDataPurpose(),
        ...getGlobalRegionFormFields(globalRegion),
    };
    data.props.userId = userId || null;
    data.props.authImport = Boolean(userId && authImport);
    data.props.pixel = getShowPixel(ctx);

    return removeUndefined(data);
};

export default Whitelable;
